import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Eye, UserPlus, Calendar, Loader2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SKIN_TEASERS } from "../lib/theme";

const SKIN_LABEL = Object.fromEntries(SKIN_TEASERS.map((s) => [s.id, s.label]));
const PATH_LABEL = { "/": "Home", "/search": "Search", "/my-list": "My List", "/settings": "Settings" };

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[var(--accent-hover)]">{icon}<span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{label}</span></div>
      <p className="mt-2 text-3xl font-bold text-[var(--text-primary)]" data-testid={`${testid}-value`}>{value}</p>
    </div>
  );
}

function BarList({ title, rows, fmt, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-5" data-testid={testid}>
      <h3 className="font-head text-lg text-[var(--text-primary)]">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--text-muted)]">No data yet.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="relative overflow-hidden rounded-md bg-[var(--bg-base)]" data-testid={`${testid}-row`}>
              <div className="absolute inset-y-0 left-0 bg-[var(--accent)]/15" style={{ width: `${(r.count / max) * 100}%` }} />
              <div className="relative flex items-center justify-between px-3 py-1.5">
                <span className="mr-2 truncate text-sm text-[var(--text-primary)]" title={fmt ? fmt(r) : r.key}>{fmt ? fmt(r) : r.key}</span>
                <span className="flex-none text-sm font-bold text-[var(--text-secondary)]">{r.count}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AnalyticsDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.is_admin) {
      api.get("/admin/analytics").then(({ data }) => setD(data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [user]);

  if (user === null) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const since = d?.since ? new Date(d.since).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
  const o = d?.overview || {};

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-8 lg:px-8" data-testid="analytics-page">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <h1 className="font-head text-4xl text-[var(--text-primary)]">Analytics</h1>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] px-3 py-1 text-xs text-[var(--text-secondary)]" data-testid="collecting-since">
            <Calendar size={13} /> Collecting since {since}
          </span>
        </div>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-[var(--text-secondary)]"><Loader2 size={16} className="animate-spin" /> Loading analytics…</div>
        ) : !d ? (
          <p className="mt-8 text-[var(--text-secondary)]">No analytics available yet.</p>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="overview-cards">
              <StatCard icon={<Users size={15} />} label="Visitors today" value={o.visitors_today} testid="stat-today" />
              <StatCard icon={<Users size={15} />} label="This week" value={o.visitors_week} testid="stat-week" />
              <StatCard icon={<Users size={15} />} label="This month" value={o.visitors_month} testid="stat-month" />
              <StatCard icon={<Eye size={15} />} label="Page views" value={o.total_pageviews} testid="stat-pageviews" />
              <StatCard icon={<UserPlus size={15} />} label="Total signups" value={o.total_signups} testid="stat-signups" />
            </div>

            <section className="mt-8 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-5" data-testid="traffic-graph">
              <h3 className="font-head text-lg text-[var(--text-primary)]">Traffic (last 30 days)</h3>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={d.traffic}>
                    <defs>
                      <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--accent)" stopOpacity={0.5} /><stop offset="100%" stopColor="var(--accent)" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
                    <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 10 }} />
                    <YAxis tick={{ fill: "var(--text-muted)", fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", color: "var(--text-primary)" }} />
                    <Area type="monotone" dataKey="views" name="Page views" stroke="var(--accent)" fill="url(#gv)" strokeWidth={2} />
                    <Area type="monotone" dataKey="visitors" name="Visitors" stroke="var(--accent-hover)" fillOpacity={0} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="mt-6 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-5" data-testid="signups-graph">
              <h3 className="font-head text-lg text-[var(--text-primary)]">Signups (last 30 days)</h3>
              <div className="mt-4 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={d.signups}>
                    <defs>
                      <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--accent-hover)" stopOpacity={0.5} /><stop offset="100%" stopColor="var(--accent-hover)" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
                    <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 10 }} />
                    <YAxis tick={{ fill: "var(--text-muted)", fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", color: "var(--text-primary)" }} />
                    <Area type="monotone" dataKey="count" name="Signups" stroke="var(--accent-hover)" fill="url(#gs)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </section>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <BarList title="Most-viewed titles" rows={d.top_titles} fmt={(r) => r.name} testid="table-titles" />
              <BarList title="Top pages" rows={d.top_pages} fmt={(r) => PATH_LABEL[r.key] || r.key} testid="table-pages" />
              <BarList title="Top search terms" rows={d.top_terms} testid="table-terms" />
              <BarList title="Top referrers" rows={d.top_referrers} fmt={(r) => r.key || "Direct"} testid="table-referrers" />
              <BarList title="Most-used skins" rows={d.top_skins} fmt={(r) => SKIN_LABEL[r.key] || r.key} testid="table-skins" />
              <BarList title="Most-tapped providers" rows={d.top_providers} testid="table-providers" />
              <BarList title="Devices" rows={d.devices} fmt={(r) => r.key[0].toUpperCase() + r.key.slice(1)} testid="table-devices" />
              <BarList title="Top countries" rows={d.countries} testid="table-countries" />
            </div>

            <p className="mt-8 text-xs text-[var(--text-muted)]">Aggregate data only — no individual browsing histories are stored or shown.</p>
          </>
        )}
      </main>
    </div>
  );
}
